import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { CourseStatus, EpisodeGenerationStatus, GenerationStatus } from '@courseai/domain';
import { useT, type I18n, type TranslationKey } from '../i18n';
import {
  GET_AGENT_GENERATION_REQUEST,
  type GetAgentGenerationRequestData,
  type GetAgentGenerationRequestVariables,
  type AgentGenerationRequest,
} from '../graphql/agent';
import {
  GET_COURSE,
  type GetCourseData,
  type GetCourseVariables,
  type CourseDetail as CourseDetailData,
  type EpisodeDetail,
} from '../graphql/courses';
import { AgentStructureReview } from './AgentStructureReview';

// How often the detail view refreshes while work is still in progress
// (proposing, or an approved course still generating scripts/media).
const POLL_INTERVAL_MS = 5000;

// Translation keys for a per-kind episode generation status (audio/video).
const EPISODE_STATUS_KEYS: Record<EpisodeGenerationStatus, TranslationKey> = {
  none: 'episodeStatus.none',
  pending: 'episodeStatus.pending',
  running: 'episodeStatus.running',
  ready: 'episodeStatus.ready',
  failed: 'episodeStatus.failed',
};

const COURSE_STATUS_KEYS: Record<CourseStatus, TranslationKey> = {
  draft: 'courseStatus.draft',
  proposed: 'courseStatus.proposed',
  approved: 'courseStatus.approved',
  generating: 'courseStatus.generating',
  ready: 'courseStatus.ready',
};

function episodeStatusLabel(t: I18n['t'], status: string): string {
  const key = EPISODE_STATUS_KEYS[status as EpisodeGenerationStatus];
  return key === undefined ? status : t(key);
}

function courseStatusLabel(t: I18n['t'], status: string): string {
  const key = COURSE_STATUS_KEYS[status as CourseStatus];
  return key === undefined ? status : t(key);
}

// True while an approved course still has media generation to complete: any
// episode with an audio/video status that is pending or running, or the course
// itself not yet fully ready.
function courseInProgress(course: CourseDetailData): boolean {
  if (course.status !== CourseStatus.ready) {
    return true;
  }
  return course.episodes.some(
    (episode) =>
      episode.audioStatus === EpisodeGenerationStatus.pending ||
      episode.audioStatus === EpisodeGenerationStatus.running ||
      episode.videoStatus === EpisodeGenerationStatus.pending ||
      episode.videoStatus === EpisodeGenerationStatus.running,
  );
}

// Draft detail screen: shows the current state of one AI course generation
// (draft) and what work is in progress. Depending on the request's stage it
// renders one of:
//   - proposing:       the agent is still researching/proposing a structure
//   - awaitingReview:  the proposed structure, editable, with an Approve action
//   - generating/ready: the created course's per-episode audio/video progress
//   - failed:          the recorded error
// It polls while work is in progress so the view updates without a manual
// refresh. Reachable from the Drafts tab.
export function DraftDetail(): ReactNode {
  const { projectId, generationRequestId } = useParams<{
    projectId: string;
    generationRequestId: string;
  }>();
  const { t } = useT();
  const navigate = useNavigate();

  const missingParams = projectId === undefined || generationRequestId === undefined;

  const {
    data: requestData,
    loading: requestLoading,
    error: requestError,
    refetch: refetchRequest,
  } = useQuery<GetAgentGenerationRequestData, GetAgentGenerationRequestVariables>(
    GET_AGENT_GENERATION_REQUEST,
    {
      variables: { projectId: projectId ?? '', generationRequestId: generationRequestId ?? '' },
      skip: missingParams,
      fetchPolicy: 'cache-and-network',
    },
  );

  const request: AgentGenerationRequest | undefined =
    requestData?.getGenerationRequest ?? undefined;
  const courseId = request?.courseId ?? undefined;

  // Once the proposal is approved it carries a courseId; load that course to
  // show per-episode generation progress.
  const { data: courseData, refetch: refetchCourse } = useQuery<GetCourseData, GetCourseVariables>(
    GET_COURSE,
    {
      variables: { projectId: projectId ?? '', courseId: courseId ?? '' },
      skip: missingParams || courseId === undefined,
      fetchPolicy: 'cache-and-network',
    },
  );

  const course = courseData?.getCourse ?? undefined;

  // Poll while work is still in progress: the request is PENDING/RUNNING
  // (proposing), or the approved course still has media generating. Stop once
  // the proposal is awaiting review, failed, or the course is fully ready.
  const stillWorking =
    request !== undefined &&
    (request.status === GenerationStatus.PENDING ||
      request.status === GenerationStatus.RUNNING ||
      (course !== undefined && courseInProgress(course)));

  const refresh = useCallback(async () => {
    await refetchRequest();
    if (courseId !== undefined) {
      await refetchCourse();
    }
  }, [refetchRequest, refetchCourse, courseId]);

  const workingRef = useRef(stillWorking);
  workingRef.current = stillWorking;
  useEffect(() => {
    if (!stillWorking) {
      return;
    }
    const timer = setInterval(() => {
      if (workingRef.current) {
        void refresh();
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [stillWorking, refresh]);

  if (missingParams) {
    return (
      <section className="screen">
        <p className="form-error">{t('drafts.detail.notSpecified')}</p>
      </section>
    );
  }

  const backLink = (
    <p>
      <Link to={`/projects/${projectId}/drafts`}>{t('drafts.detail.back')}</Link>
    </p>
  );

  if (requestError) {
    return (
      <section className="screen">
        {backLink}
        <p className="form-error">
          {t('drafts.detail.loadError', { message: requestError.message })}
        </p>
      </section>
    );
  }

  if (request === undefined) {
    return (
      <section className="screen">
        {backLink}
        <p>{requestLoading ? t('drafts.detail.loading') : t('drafts.detail.notFound')}</p>
      </section>
    );
  }

  // FAILED proposal.
  if (request.status === GenerationStatus.FAILED) {
    return (
      <section className="screen">
        {backLink}
        <div className="screen-header">
          <h2>{t('drafts.detail.title')}</h2>
          <span className="draft-stage draft-stage-failed">{t('drafts.stage.failed')}</span>
        </div>
        <p className="form-error">
          {request.errorDescription ?? t('drafts.detail.failedNoDescription')}
        </p>
      </section>
    );
  }

  const inProposeStage =
    courseId === undefined &&
    (request.status === GenerationStatus.PENDING || request.status === GenerationStatus.RUNNING);

  // PENDING / RUNNING without a courseId — the agent is still researching and
  // proposing a structure.
  if (inProposeStage) {
    return (
      <section className="screen">
        {backLink}
        <div className="screen-header">
          <h2>{t('drafts.detail.title')}</h2>
          <span className="draft-stage draft-stage-proposing">{t('drafts.stage.proposing')}</span>
        </div>
        <p>{t('drafts.detail.proposing')}</p>
      </section>
    );
  }

  // READY without a courseId — the proposal awaits review/approval.
  if (courseId === undefined) {
    return (
      <section className="screen">
        {backLink}
        <div className="screen-header">
          <h2>{t('drafts.detail.title')}</h2>
          <span className="draft-stage draft-stage-awaitingReview">
            {t('drafts.stage.awaitingReview')}
          </span>
        </div>
        <AgentStructureReview
          projectId={projectId}
          request={request}
          onApproved={(created) => {
            // Approval creates a NEW job (with the new courseId) that does the
            // script writing. Navigate to that job's draft so the view tracks
            // the live script-generation progress instead of staying on this
            // (now-superseded) proposal.
            navigate(`/projects/${projectId}/drafts/${created.generationRequestId}`);
          }}
        />
      </section>
    );
  }

  // courseId is set. Two sub-states:
  //  - the approve job is still PENDING/RUNNING → scripts are being written and
  //    the Course row does not exist yet (it is persisted transactionally only
  //    after every script succeeds). Show a clear "generating scripts" panel.
  //  - the approve job is READY → the Course + episodes exist; show them.
  const scriptsGenerating =
    request.status === GenerationStatus.PENDING || request.status === GenerationStatus.RUNNING;

  if (scriptsGenerating || course === undefined) {
    return (
      <section className="screen">
        {backLink}
        <div className="screen-header">
          <h2>{t('drafts.detail.title')}</h2>
          <span className="draft-stage draft-stage-generating">{t('drafts.stage.generating')}</span>
        </div>

        <div className="draft-generating-panel">
          <div
            className="draft-progress-bar"
            role="progressbar"
            aria-label={t('drafts.stage.generating')}
          >
            <span className="draft-progress-bar-fill" />
          </div>
          <p className="draft-generating-lead">{t('drafts.detail.scriptsGenerating')}</p>
          <ul className="draft-generating-steps">
            <li>{t('drafts.detail.stepScripts')}</li>
            <li>{t('drafts.detail.stepCourse')}</li>
            <li>{t('drafts.detail.stepMedia')}</li>
          </ul>
          <p className="field-hint">{t('drafts.detail.autoRefresh')}</p>
        </div>
      </section>
    );
  }

  // READY with a courseId and the Course loaded — show the created course, each
  // episode's generated script status, and (per-episode) audio/video status.
  return (
    <section className="screen">
      {backLink}
      <div className="screen-header">
        <h2>{course.title}</h2>
        <span className={`course-status course-status-${course.status}`}>
          {courseStatusLabel(t, course.status)}
        </span>
      </div>

      <p className="field-hint">
        {courseInProgress(course) ? t('drafts.detail.generating') : t('drafts.detail.generated')}
      </p>
      <p className="field-hint">{t('drafts.detail.whatGenerated')}</p>

      <ul className="draft-episode-list">
        {course.episodes.map((episode: EpisodeDetail) => (
          <li key={episode.episodeIndex} className="draft-episode-row">
            <span className="draft-episode-index">{episode.episodeIndex + 1}</span>
            <span className="draft-episode-title">{episode.title}</span>
            <span className="draft-episode-progress">
              <span className="draft-episode-kind">
                {t('drafts.detail.script')}:{' '}
                {episode.script ? t('drafts.detail.scriptReady') : t('drafts.detail.scriptPending')}
              </span>
              <span className={`episode-status episode-status-${episode.audioStatus}`}>
                {t('drafts.detail.audio')}: {episodeStatusLabel(t, episode.audioStatus)}
              </span>
              <span className={`episode-status episode-status-${episode.videoStatus}`}>
                {t('drafts.detail.video')}: {episodeStatusLabel(t, episode.videoStatus)}
              </span>
            </span>
          </li>
        ))}
      </ul>

      <div className="draft-detail-actions">
        <button
          type="button"
          onClick={() => navigate(`/projects/${projectId}/courses/${course.courseId}`)}
        >
          {t('drafts.detail.openCourse')}
        </button>
      </div>
    </section>
  );
}
